import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faXmark,
  faTrash,
  faStar,
  faRightFromBracket,
  faRotateRight,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import adminApi, { clearSession, getAdmin } from "./api";
import { bookTitle } from "./bookTitles";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const admin = getAdmin();

  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [book, setBook] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, counts] = await Promise.all([
        adminApi.get(`/admin/reviews?status=${tab}`),
        adminApi.get("/admin/reviews/stats"),
      ]);
      setReviews(list.data);
      setStats(counts.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load reviews");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await adminApi.patch(`/admin/reviews/${id}/status`, { status });
      // Drop it from the current tab immediately — it now belongs to another one
      setReviews((prev) => prev.filter((r) => r._id !== id));
      setStats((prev) => ({
        ...prev,
        [tab]: Math.max(0, prev[tab] - 1),
        [status]: prev[status] + 1,
      }));
      toast.success(status === "approved" ? "Review approved" : "Review rejected");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    setBusyId(id);
    try {
      await adminApi.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r._id !== id));
      setStats((prev) => ({ ...prev, [tab]: Math.max(0, prev[tab] - 1) }));
      toast.success("Review deleted");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const logout = () => {
    clearSession();
    navigate("/admin/login");
  };

  const books = Array.from(new Set(reviews.map((r) => r.productId)));
  const visible =
    book === "all" ? reviews : reviews.filter((r) => r.productId === book);

  return (
    <div className="w-full min-h-dvh bg-[#1b1d1f] text-white">
      <header className="bg-gradient-mn px-4 md:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="times-new-roman text-[22px] md:text-[26px] font-bold">
            Sean McFirnn Admin
          </h1>
          <p className="text-[#D2C7D2] text-[12px]">
            {admin?.email} — review moderation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="rounded-md bg-[#ffffff2a] hover:bg-[#ffffff3a] px-3 py-2 text-[13px] flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faRotateRight} /> Refresh
          </button>
          <button
            onClick={logout}
            className="rounded-md bg-[#ffffff2a] hover:bg-red-500/80 px-3 py-2 text-[13px] flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faRightFromBracket} /> Logout
          </button>
        </div>
      </header>

      <div className="px-4 md:px-8 py-5 flex flex-col gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setBook("all");
              }}
              className={`rounded-md px-4 py-2 text-[14px] font-bold transition-colors ${
                tab === t.key
                  ? "bg-[#2aa9a0] text-white"
                  : "bg-[#ffffff14] text-[#D2C7D2] hover:bg-[#ffffff24]"
              }`}
            >
              {t.label}
              <span className="ml-2 text-[12px] opacity-80">{stats[t.key]}</span>
            </button>
          ))}

          {books.length > 1 && (
            <select
              value={book}
              onChange={(e) => setBook(e.target.value)}
              className="ml-auto rounded-md bg-[#ffffff14] px-3 py-2 text-[13px] outline-none"
            >
              <option value="all">All books</option>
              {books.map((id) => (
                <option key={id} value={id}>
                  {bookTitle(id)}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-[#D2C7D2]">
            <CircularProgress size={20} sx={{ color: "#2aa9a0" }} /> Loading
            reviews...
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center text-[#D2C7D2] py-20">
            {tab === "pending"
              ? "Nothing waiting for approval."
              : `No ${tab} reviews.`}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((review) => (
              <div
                key={review._id}
                className="bg-[#ffffff14] rounded-lg p-4 flex flex-col md:flex-row md:items-start gap-4"
              >
                <div className="w-[42px] h-[42px] flex-shrink-0 rounded-full bg-[#2aa9a0] flex items-center justify-center text-[18px] font-bold">
                  {review.name ? review.name.charAt(0).toUpperCase() : "?"}
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-[15px]">{review.name}</span>
                    <span className="flex items-center gap-1 text-[13px] text-yellow-400">
                      <FontAwesomeIcon icon={faStar} /> {review.rating}
                    </span>
                    {review.email && (
                      <span className="text-[12px] text-[#D2C7D2]">
                        {review.email}
                      </span>
                    )}
                    {review.address && (
                      <span className="text-[12px] text-[#D2C7D2] flex items-center gap-1">
                        <FontAwesomeIcon icon={faLocationDot} /> {review.address}
                      </span>
                    )}
                  </div>

                  <div className="text-[12px] text-[#2aa9a0] font-bold">
                    {bookTitle(review.productId)}
                  </div>

                  <p className="text-[14px] break-words">{review.message}</p>

                  <div className="text-[11px] text-[#9d9d9d]">
                    Submitted{" "}
                    {new Date(review.createdAt).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2 md:flex-col md:items-stretch">
                  {tab !== "approved" && (
                    <button
                      disabled={busyId === review._id}
                      onClick={() => setStatus(review._id, "approved")}
                      className="rounded-md bg-[#2aa9a0] hover:bg-[#22908a] disabled:opacity-50 px-3 py-2 text-[13px] font-bold flex items-center gap-2 whitespace-nowrap"
                    >
                      <FontAwesomeIcon icon={faCheck} /> Approve
                    </button>
                  )}
                  {tab !== "rejected" && (
                    <button
                      disabled={busyId === review._id}
                      onClick={() => setStatus(review._id, "rejected")}
                      className="rounded-md bg-[#ffffff2a] hover:bg-red-500/80 disabled:opacity-50 px-3 py-2 text-[13px] font-bold flex items-center gap-2 whitespace-nowrap"
                    >
                      <FontAwesomeIcon icon={faXmark} /> Reject
                    </button>
                  )}
                  <button
                    disabled={busyId === review._id}
                    onClick={() => remove(review._id)}
                    className="rounded-md bg-[#ffffff14] hover:bg-red-600/80 disabled:opacity-50 px-3 py-2 text-[13px] flex items-center gap-2 whitespace-nowrap"
                  >
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
